import { t } from "@lingui/macro";
import type { TestSmtpConnectionInput } from "@src/__generated__/graphql";
import type {
  CreateWorkspaceSmtpSettingsInput,
  Maybe,
  UpdateWorkspaceSmtpSettingsInput,
  WorkspaceSettingsQuery,
} from "@src/__generated__/urql-graphql";
import type { SettingsModalStore } from "@src/stores/SettingsModalStore";
import { email, required } from "@src/utils/forms/validators";
import { FieldState, FormState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

export enum SmtpEncryptionEnum {
  None = "none",
  Ssl = "ssl",
  Tls = "tls",
  SslTls = "ssl/tls",
  Starttls = "starttls",
}

export class SmtpSettingsState {
  /**
   * Validator that only runs when the form is dirty
   */
  private requiredIfNotEmpty = (value: string | SmtpEncryptionEnum) => {
    if (this.settingsStore.smtpStore.isOauthConnected) return null;
    if (this.isEmpty) return null;
    return required(value);
  };

  private requiredIfUpdating = (value: string) => {
    if (this.settingsStore.smtpStore.isOauthConnected) return null;
    if (!this.settingsStore.smtpStore.smtpSettingsId)
      return this.requiredIfNotEmpty(value);
    return null;
  };

  @observable
  readonly form = new FormState({
    active: new FieldState(true),
    smtp_host: new FieldState("").validators(this.requiredIfNotEmpty),
    smtp_port: new FieldState("").validators(this.requiredIfNotEmpty),
    smtp_username: new FieldState("").validators(this.requiredIfNotEmpty),
    smtp_password: new FieldState("").validators(this.requiredIfUpdating),
    smtp_encryption: new FieldState<SmtpEncryptionEnum>(SmtpEncryptionEnum.Ssl),
    smtp_from_address: new FieldState("").validators(
      this.requiredIfNotEmpty,
      email,
    ),
    smtp_from_name: new FieldState("").validators(this.requiredIfNotEmpty),
  });

  constructor(private settingsStore: SettingsModalStore) {
    makeObservable(this);
  }

  @computed
  get isDirty(): boolean {
    return (
      !!this.form.$.active.dirty ||
      !!this.form.$.smtp_encryption.dirty ||
      !!this.form.$.smtp_from_address.dirty ||
      !!this.form.$.smtp_from_name.dirty ||
      !!this.form.$.smtp_host.dirty ||
      !!this.form.$.smtp_port.dirty ||
      !!this.form.$.smtp_username.dirty ||
      !!this.form.$.smtp_password.dirty
    );
  }

  @computed
  get isEmpty(): boolean {
    return (
      !this.form.$.smtp_from_address.value &&
      !this.form.$.smtp_from_name.value &&
      !this.form.$.smtp_host.value &&
      !this.form.$.smtp_port.value &&
      !this.form.$.smtp_username.value &&
      !this.form.$.smtp_password.value
    );
  }

  @action.bound
  init(
    settings:
      | Maybe<WorkspaceSettingsQuery["workspaceSmtpSettings"]>
      | undefined,
  ) {
    if (!settings) {
      this.form.reset();
      return;
    }

    this.form.$.smtp_password.reset();
    this.form.$.active.reset(settings.active);
    this.form.$.smtp_host.reset(settings.smtp_host ?? "");
    this.form.$.smtp_port.reset(settings.smtp_port?.toString() ?? "");
    this.form.$.smtp_username.reset(settings.smtp_username ?? "");
    this.form.$.smtp_encryption.reset(
      settings.smtp_encryption as SmtpEncryptionEnum,
    );
    this.form.$.smtp_from_address.reset(settings.smtp_from_address ?? "");
    this.form.$.smtp_from_name.reset(settings.smtp_from_name ?? "");
  }

  async getCreateMutationInput(
    forTest?: boolean,
  ): Promise<CreateWorkspaceSmtpSettingsInput | undefined> {
    const validation = await this.form.validate();

    if (validation.hasError) return;

    if (forTest && !validation.value.smtp_password.$) {
      this.form.$.smtp_password.setError(t`Required field`);
      return;
    }

    return {
      active: validation.value.active.$,
      smtp_host: validation.value.smtp_host.$,
      smtp_port: Number(validation.value.smtp_port.$),
      smtp_username: validation.value.smtp_username.$,
      smtp_password: validation.value.smtp_password.$,
      smtp_encryption: validation.value.smtp_encryption.$,
      smtp_from_address: validation.value.smtp_from_address.$,
      smtp_from_name: validation.value.smtp_from_name.$,
    };
  }

  async getUpdateMutationInput(): Promise<
    UpdateWorkspaceSmtpSettingsInput | undefined
  > {
    const validation = await this.form.validate();

    if (validation.hasError) return;

    return {
      active: validation.value.active.$,
      smtp_host: validation.value.smtp_host.$,
      smtp_port: Number(validation.value.smtp_port.$),
      smtp_username: validation.value.smtp_username.$,
      smtp_password:
        validation.value.smtp_password.$ === ""
          ? undefined
          : validation.value.smtp_password.$,
      smtp_encryption: validation.value.smtp_encryption.$,
      smtp_from_address: validation.value.smtp_from_address.$,
      smtp_from_name: validation.value.smtp_from_name.$,
    };
  }

  async getTestConnectionInput(): Promise<TestSmtpConnectionInput | undefined> {
    if (this.isEmpty) return;

    const validation = await this.form.validate();

    if (validation.hasError) return;

    return {
      smtp_host: validation.value.smtp_host.$,
      smtp_port: Number(validation.value.smtp_port.$),
      smtp_username: validation.value.smtp_username.$,
      smtp_password: validation.value.smtp_password.$,
      smtp_encryption: validation.value.smtp_encryption.$,
    };
  }
}
